/**
 * A client's AI assistant: whether it may have one, what its AI is charged at,
 * and what AI took from its one wallet.
 *
 * Off by default, and off means the client has no trace of it. The AI gateway
 * meters tokens and keeps no money, so the money is Connect's: the
 * assistant's model calls are charged one by one to the client's wallet — the
 * one every service is paid from, topped up under Wallet & credits — and an
 * empty wallet hands the client's conversations to its people.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Plus, Trash2 } from 'lucide-react';
import {
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Select,
  StatusChip,
  Switch,
  api,
  formatDateTime,
  formatMoney,
  toast,
  useCan,
} from '@connect/web-shared';

import { ServiceSpend } from './service-spend';

export interface AiWalletEntry {
  public_id: string;
  kind: 'top_up' | 'charge' | 'refund' | 'adjustment';
  amount: string;
  balance_after: string;
  description: string;
  /** Staff only: the model that answered — never in the client's words. */
  usage?: { model?: string | null };
  at: string;
}

/**
 * An entry's words, with the model that answered when it was an AI call. The
 * model is kept in ``usage`` and shown only here: a client is never told it.
 */
export function entryWords(entry: { description: string; usage?: { model?: string | null } }): string {
  return entry.usage?.model ? `${entry.description} · ${entry.usage.model}` : entry.description;
}

export interface RateCard {
  currency: 'INR';
  input_per_million: string;
  output_per_million: string;
  models: { match: string; input_per_million: string; output_per_million: string }[];
}

export interface AiClient {
  public_id: string;
  name: string;
  enabled: boolean;
  /** Its assistant may act on other clients' Connect accounts for them. */
  account_help: boolean;
  /** Its own rate card, or null for the platform's. */
  own_pricing: RateCard | null;
  wallet: {
    /** What it is charged at now: its own card, or the platform's. */
    pricing: RateCard;
    custom_pricing: boolean;
    balance: string;
    currency: string;
    low_balance_threshold: string | null;
    low: boolean;
    spent_today: string;
    spent_30_days: string;
    entries: AiWalletEntry[];
  };
}

export const aiKeys = {
  all: ['ai'] as const,
  overview: ['ai', 'overview'] as const,
  gateway: ['ai', 'gateway'] as const,
  clients: ['ai', 'clients'] as const,
  client: (id: string) => ['ai', 'client', id] as const,
};

/** A metered amount to four places, string-based like `formatMoney`. */
export function formatMetered(value: string): string {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace(/^-/, '').split('.');
  const shown = `₹${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction.padEnd(4, '0').slice(0, 4)}`;
  return negative ? `−${shown}` : shown;
}

const KIND_LABELS = { top_up: 'Top-up', charge: 'AI use', refund: 'Refund', adjustment: 'Adjustment' };

export function AiCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.ai.edit');
  const url = `/messaging/ai/clients/${clientPublicId}`;
  const key = aiKeys.client(clientPublicId);
  const ai = useQuery({ queryKey: key, queryFn: () => api.get<AiClient>(url) });

  const update = useMutation({
    mutationFn: (body: { enabled?: boolean; account_help?: boolean; pricing?: RateCard | null }) =>
      api.patch<AiClient>(url, body),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      void queryClient.invalidateQueries({ queryKey: aiKeys.clients });
      toast.success('Saved');
    },
    onError: (e) => toast.error('Could not change it', e instanceof ApiError ? e.message : undefined),
  });

  if (ai.data === undefined) return <QueryPending query={ai} label="Loading AI" />;
  const data = ai.data;
  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Bot className="size-4" />
              AI assistant
            </span>
          }
          description={`Whether ${clientName} may have an AI assistant on its services, what its AI is charged at, and what AI took from its wallet.`}
          actions={
            <Switch
              label={data.enabled ? 'On' : 'Off'}
              checked={data.enabled} disabled={update.isPending}
              onChange={(e) => update.mutate({ enabled: e.target.checked })}
            />
          }
        />
        <CardBody className="space-y-3">
          {data.enabled ? (
            <div className="flex flex-wrap items-start justify-between gap-3 rounded-control border border-line p-3">
              <div className="min-w-0 max-w-prose">
                <p className="text-sm font-medium text-heading">Account help for Connect clients</p>
                <p className="text-xs text-muted">
                  For the line Connect's own clients write to. When a client writes from their account's sign-in mobile, this assistant
                  can look up their services and templates and send a message from their account — charged to their balance, and only
                  after they confirm. Leave it off for everyone else.
                </p>
              </div>
              <Switch
                label={data.account_help ? 'On' : 'Off'}
                checked={data.account_help} disabled={update.isPending}
                onChange={(e) => update.mutate({ account_help: e.target.checked })}
              />
            </div>
          ) : null}
          <ServiceSpend
            balance={data.wallet.balance}
            currency={data.wallet.currency}
            low={data.wallet.low}
            spent={data.wallet.spent_30_days}
          />
          <p className="text-xs text-muted">Today: {formatMoney(data.wallet.spent_today, data.wallet.currency)}</p>
          <PricingEditor
            current={data.own_pricing}
            effective={data.wallet.pricing}
            canEdit={canEdit}
            saving={update.isPending}
            onSave={(pricing) => update.mutate({ pricing })}
          />
          <p className="text-xs font-medium text-muted">AI's latest movements</p>
          {data.wallet.entries.length === 0 ? (
            <p className="text-sm text-muted">No movements yet.</p>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {data.wallet.entries.slice(0, 10).map((entry) => (
                  <tr key={entry.public_id} className="border-t border-line">
                    <td className="py-1.5 text-muted">{formatDateTime(entry.at)}</td>
                    <td className="py-1.5">
                      <StatusChip value={entry.kind} labels={KIND_LABELS}
                        tones={{ top_up: 'success', refund: 'success', charge: 'neutral', adjustment: 'info' }} />
                    </td>
                    <td className="max-w-xs truncate py-1.5 text-muted">{entryWords(entry)}</td>
                    <td className="py-1.5 text-right font-mono tabular">{formatMetered(entry.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

/**
 * What this client pays per million tokens: the platform's rate card, or its
 * own — a price agreed with it. Only the charges made after a change use it.
 */
function PricingEditor({
  current,
  effective,
  canEdit,
  saving,
  onSave,
}: {
  current: RateCard | null;
  effective: RateCard;
  canEdit: boolean;
  saving: boolean;
  onSave: (pricing: RateCard | null) => void;
}) {
  const [own, setOwn] = useState(current !== null);
  const [card, setCard] = useState<RateCard>(current ?? effective);
  const setModel = (index: number, patch: Partial<RateCard['models'][number]>) =>
    setCard((c) => ({ ...c, models: c.models.map((m, i) => (i === index ? { ...m, ...patch } : m)) }));
  const changed = own !== (current !== null) || (own && JSON.stringify(card) !== JSON.stringify(current));
  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-2 rounded border border-line p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-medium text-muted">Price per million tokens</p>
          <Select
            size="sm"
            containerClassName="w-64"
            aria-label="Rate card"
            value={own ? 'own' : 'platform'}
            options={[
              { value: 'platform', label: "The platform's rate card" },
              { value: 'own', label: 'Its own rate card' },
            ]}
            onChange={(e) => {
              const next = e.target.value === 'own';
              setOwn(next);
              if (next && !current) setCard(effective);
            }}
          />
        </div>
        {own ? (
          <div className="space-y-2">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input size="sm" label="Input (₹ / million)" inputMode="decimal"
                value={card.input_per_million} onChange={(e) => setCard({ ...card, input_per_million: e.target.value })} />
              <Input size="sm" label="Output (₹ / million)" inputMode="decimal"
                value={card.output_per_million} onChange={(e) => setCard({ ...card, output_per_million: e.target.value })} />
            </div>
            {card.models.map((rate, index) => (
              <div key={index} className="flex items-end gap-2">
                <Input size="sm" label="Model prefix" className="font-mono" containerClassName="flex-1"
                  value={rate.match} onChange={(e) => setModel(index, { match: e.target.value })} />
                <Input size="sm" label="Input" inputMode="decimal" containerClassName="w-24"
                  value={rate.input_per_million} onChange={(e) => setModel(index, { input_per_million: e.target.value })} />
                <Input size="sm" label="Output" inputMode="decimal" containerClassName="w-24"
                  value={rate.output_per_million} onChange={(e) => setModel(index, { output_per_million: e.target.value })} />
                {canEdit ? (
                  <Button size="sm" variant="ghost" aria-label="Remove"
                    onClick={() => setCard({ ...card, models: card.models.filter((_, i) => i !== index) })}>
                    <Trash2 className="size-3.5" />
                  </Button>
                ) : null}
              </div>
            ))}
            {canEdit ? (
              <Button size="sm" variant="ghost"
                onClick={() => setCard({ ...card, models: [...card.models, { match: '', input_per_million: '0', output_per_million: '0' }] })}>
                <Plus className="size-3.5" />
                Rate for a model
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-muted">
            {formatMoney(effective.input_per_million)} input · {formatMoney(effective.output_per_million)} output
            {effective.models.length ? `, ${effective.models.length} model rate(s)` : ''} — set in AI automation.
          </p>
        )}
        {canEdit && changed ? (
          <div className="flex justify-end">
            <Button size="sm" loading={saving} onClick={() => onSave(own ? card : null)}>
              Save the price
            </Button>
          </div>
        ) : null}
      </div>
    </ReadOnly>
  );
}
